import React, { useMemo, useState } from 'react';
import { APP_VERSION } from '../config/release';
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from './icons/AppIcon';
import { colors, spacing, typography } from '../theme';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import * as Application from 'expo-application';
import * as Updates from 'expo-updates';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useSettingsAccess } from '../hooks/useSettingsAccess';
import { ConfirmationModal } from './Modal';
import { withAlpha } from '../theme';
import {
  SETTINGS_ENTRIES,
  SETTINGS_GROUPS,
  SettingsEntry,
  settingsEntryForScreen,
} from '../config/settingsCatalog';

interface SettingsContentProps {
  /** Panel entries (profile, phone, notifications, developer) — the Settings screen opens them. */
  onItemPress?: (itemId: string) => void;
  showHeader?: boolean;
  onClose?: () => void;
  variant?: 'pane' | 'screen';
  activeItemId?: string | null;
}

/**
 * The settings list — the mobile Settings screen and the web side pane.
 * Entries, groups and who sees what come from config/settingsCatalog.ts.
 */
export const SettingsContent: React.FC<SettingsContentProps> = ({
  onItemPress,
  showHeader = true,
  onClose,
  variant = 'pane',
  activeItemId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness, logout, setUiLocale } = useAppStore();
  const { navigateTo, currentScreen, showNotification } = useAppContext();
  const { locale, t } = useLocale();
  const { themeMode, setThemeMode } = useTheme();
  const rtl = useRTLStyles();
  const { role, isAdmin, hasModule } = useSettingsAccess();

  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'available' | 'up-to-date'>('idle');

  // A.B.C.D — store release + live-update number (release/version.json),
  // with the native build number for support.
  const appVersion = Platform.OS === 'web'
    ? `${APP_VERSION} (web)`
    : `${APP_VERSION}${Application.nativeBuildVersion ? ` (${Application.nativeBuildVersion})` : ''}`;

  // Which JavaScript is running: the code shipped in the store build, or a
  // live update (published date + commit, see scripts/publish-update.js)
  const updateLabel = (() => {
    if (Platform.OS === 'web') return '';
    try {
      if (Updates.isEmbeddedLaunch || !Updates.updateId) return t('settings.version.builtIn');
      const info = (Updates.manifest as any)?.extra?.dislix || {};
      const when = Updates.createdAt ? Updates.createdAt.toISOString().slice(0, 10) : '';
      return t('settings.version.update', { date: when, commit: String(info.commit || Updates.updateId).slice(0, 7) });
    } catch {
      return '';
    }
  })();

  const handleCheckUpdate = async () => {
    if (Platform.OS === 'web') return;
    setIsCheckingUpdate(true);
    setUpdateStatus('idle');
    try {
      const result = await Updates.checkForUpdateAsync();
      if (result.isAvailable) {
        setUpdateStatus('available');
        Alert.alert(
          t('settings.update.title'),
          t('settings.update.message'),
          [
            { text: t('settings.update.later'), style: 'cancel', onPress: () => setUpdateStatus('idle') },
            {
              text: t('settings.update.now'),
              onPress: async () => {
                await Updates.fetchUpdateAsync();
                await Updates.reloadAsync();
              },
            },
          ]
        );
      } else {
        setUpdateStatus('up-to-date');
      }
    } catch {
      setUpdateStatus('up-to-date');
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  // A confirmation modal (works on web too — Alert buttons don't).
  const deleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const result = await apiService.deleteAccount();
      if (result.statusCode === 1000) {
        setConfirmDelete(false);
        showNotification('success', t('settings.deleteAccount.successTitle'), t('settings.deleteAccount.successMessage'));
        if (logout) logout();
        navigateTo('welcome');
      } else {
        showNotification('error', t('settings.deleteAccount.errorTitle'), result.errors?.[0]?.message || t('settings.deleteAccount.errorMessage'));
      }
    } catch {
      showNotification('error', t('settings.deleteAccount.errorTitle'), t('settings.deleteAccount.errorMessage'));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Highlighted entry: the open panel, or the entry owning the current screen.
  const activeId = activeItemId || settingsEntryForScreen(currentScreen)?.id || null;

  const query = search.trim().toLowerCase();
  const groups = useMemo(() => SETTINGS_GROUPS.map((group) => ({
    group,
    entries: SETTINGS_ENTRIES.filter((e) => e.group === group && (!e.admin || isAdmin)).filter((e) => {
      if (!query) return true;
      return [t(`settings.entry.${e.id}.title` as any), t(`settings.entry.${e.id}.description` as any)]
        .some((text) => String(text).toLowerCase().includes(query));
    }),
  })).filter((g) => g.entries.length > 0), [isAdmin, query, t]);

  const open = (entry: SettingsEntry) => {
    if (!hasModule(entry.module)) {
      navigateTo('billing-addons');
      return;
    }
    if (entry.screen) navigateTo(entry.screen);
    else if (entry.panel && onItemPress) onItemPress(entry.panel === 'developer' ? 'developer' : entry.panel);
  };

  const isScreenVariant = variant === 'screen';
  const chevronIcon = rtl.mirrorIcon('chevron-forward');

  const renderEntry = (entry: SettingsEntry) => {
    const locked = !hasModule(entry.module);
    const active = activeId === entry.id;
    return (
      <TouchableOpacity
        key={entry.id}
        style={[styles.card, { flexDirection: rtl.rowDirection }, active && styles.cardActive]}
        activeOpacity={0.8}
        onPress={() => open(entry)}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
      >
        <View style={[styles.cardIconWrapper, active && styles.cardIconWrapperActive, locked && styles.lockedIcon]}>
          <AppIcon name={entry.icon} size={20} color={(active ? colors.white : locked ? colors.text.secondary : colors.primary) as string} />
        </View>
        <View style={styles.cardContent}>
          <Text style={[styles.cardTitle, { textAlign: rtl.textStart }, active && styles.cardTitleActive, locked && styles.lockedText]} numberOfLines={1}>
            {t(`settings.entry.${entry.id}.title` as any)}
          </Text>
          <Text style={[styles.cardDescription, { textAlign: rtl.textStart }]} numberOfLines={2}>
            {t(`settings.entry.${entry.id}.description` as any)}
          </Text>
        </View>
        {locked ? (
          <View style={[styles.upgradePill, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="lock-closed" size={12} color={colors.primary as string} />
            <Text style={styles.upgradeText}>{t('settings.upgrade')}</Text>
          </View>
        ) : active ? (
          <Ionicons name="checkmark-circle" size={18} color={colors.primary as string} />
        ) : (
          <Ionicons name={chevronIcon as any} size={18} color={colors.text.secondary as string} />
        )}
      </TouchableOpacity>
    );
  };

  const segmented = (options: Array<{ key: string; label: string }>, value: string, onSelect: (key: string) => void) => (
    <View style={[styles.langToggle, { flexDirection: rtl.rowDirection }]}>
      {options.map((o) => (
        <TouchableOpacity
          key={o.key}
          style={[styles.langOption, value === o.key && styles.langOptionActive]}
          onPress={() => onSelect(o.key)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityState={{ selected: value === o.key }}
        >
          <Text style={[styles.langOptionText, value === o.key && styles.langOptionTextActive]}>{o.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <ScrollView
      style={isScreenVariant ? styles.screenContainer : styles.paneContainer}
      contentContainerStyle={isScreenVariant ? styles.screenScrollContent : styles.paneScrollContent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {showHeader && (
        <View style={[isScreenVariant ? styles.screenHeader : styles.paneHeader, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.headerText}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('settings.header.title')}</Text>
            <Text style={[styles.subtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {currentBusiness?.name
                ? `${currentBusiness.name}${role ? ` · ${t(`settings.role.${role}` as any)}` : ''}`
                : t('settings.header.subtitle')}
            </Text>
          </View>
          {onClose && (
            <TouchableOpacity style={styles.closeButton} onPress={onClose} accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={18} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </View>
      )}

      <View style={[styles.searchRow, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="search-outline" size={16} color={colors.text.secondary as string} />
        <TextInput
          style={[styles.searchInput, { textAlign: rtl.textStart }]}
          value={search}
          onChangeText={setSearch}
          placeholder={t('settings.search')}
          placeholderTextColor={colors.text.secondary as string}
          autoCorrect={false}
          autoCapitalize="none"
        />
        {search.length > 0 && (
          <TouchableOpacity onPress={() => setSearch('')} hitSlop={8}>
            <Ionicons name="close-circle" size={16} color={colors.text.secondary as string} />
          </TouchableOpacity>
        )}
      </View>

      {!query && (
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { textAlign: rtl.textStart }]}>{t('settings.group.preferences')}</Text>
          <View style={[styles.card, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.cardIconWrapper}>
              <Ionicons name="language-outline" size={20} color={colors.primary as string} />
            </View>
            <View style={styles.cardContent}>
              <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('settings.language.title')}</Text>
            </View>
            {segmented([{ key: 'en', label: 'EN' }, { key: 'ar', label: 'عربي' }], locale, (k) => setUiLocale(k as 'en' | 'ar'))}
          </View>
          <View style={[styles.card, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.cardIconWrapper}>
              <Ionicons name={themeMode === 'dark' ? 'moon-outline' : 'sunny-outline'} size={20} color={colors.primary as string} />
            </View>
            <View style={styles.cardContent}>
              <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('settings.theme.title')}</Text>
            </View>
            {segmented([{ key: 'light', label: t('settings.theme.light') }, { key: 'dark', label: t('settings.theme.dark') }], themeMode, (k) => setThemeMode(k as 'light' | 'dark'))}
          </View>
        </View>
      )}

      {groups.map(({ group, entries }) => (
        <View key={group} style={styles.section}>
          <Text style={[styles.sectionLabel, { textAlign: rtl.textStart }]}>{t(`settings.group.${group}` as any)}</Text>
          {entries.map(renderEntry)}
        </View>
      ))}

      {query && groups.length === 0 && (
        <Text style={[styles.emptyText, { textAlign: rtl.textStart }]}>{t('settings.noResults')}</Text>
      )}

      {!query && (
        <>
          <View style={styles.divider} />
          <TouchableOpacity
            style={[styles.card, styles.dangerCard, { flexDirection: rtl.rowDirection }]}
            activeOpacity={0.8}
            onPress={() => setConfirmDelete(true)}
            disabled={isDeletingAccount}
          >
            <View style={[styles.cardIconWrapper, styles.dangerIcon]}>
              <Ionicons name="trash-outline" size={20} color={colors.error as string} />
            </View>
            <View style={styles.cardContent}>
              <Text style={[styles.cardTitle, styles.dangerText, { textAlign: rtl.textStart }]}>{t('settings.deleteAccount.cardTitle')}</Text>
              <Text style={[styles.cardDescription, { textAlign: rtl.textStart }]}>{t('settings.deleteAccount.cardDescription')}</Text>
            </View>
            <Ionicons name={chevronIcon as any} size={18} color={colors.error as string} />
          </TouchableOpacity>

          <View style={[styles.versionRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={styles.versionText}>
              {t('settings.version.label', { version: appVersion })}{updateLabel ? ` · ${updateLabel}` : ''}
            </Text>
            {Platform.OS !== 'web' && (
              <TouchableOpacity style={[styles.updateBtn, { flexDirection: rtl.rowDirection }]} onPress={handleCheckUpdate} disabled={isCheckingUpdate}>
                {isCheckingUpdate ? (
                  <ActivityIndicator size="small" color={colors.primary as string} />
                ) : updateStatus === 'up-to-date' ? (
                  <>
                    <Ionicons name="checkmark-circle-outline" size={14} color={colors.success as string} />
                    <Text style={[styles.updateBtnText, { color: colors.success as string }]}>{t('settings.version.upToDate')}</Text>
                  </>
                ) : (
                  <>
                    <Ionicons name="refresh-outline" size={14} color={colors.primary as string} />
                    <Text style={styles.updateBtnText}>{t('settings.version.checkForUpdate')}</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        </>
      )}

      <ConfirmationModal
        visible={confirmDelete}
        onClose={() => { if (!isDeletingAccount) setConfirmDelete(false); }}
        onConfirm={deleteAccount}
        title={t('settings.deleteAccount.title')}
        message={t('settings.deleteAccount.confirmMessage')}
        confirmText={t('settings.deleteAccount.confirm')}
        cancelText={t('settings.deleteAccount.cancel')}
        type="error"
        loading={isDeletingAccount}
      />
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  headerText: { flex: 1, minWidth: 0 },
  searchRow: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'web' ? spacing.sm : spacing.xs,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
    marginBottom: spacing.lg,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    paddingVertical: Platform.OS === 'android' ? spacing.xs : 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  } as any,
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, paddingVertical: spacing.lg },
  lockedIcon: { backgroundColor: withAlpha(colors.text.secondary, 0.08) },
  lockedText: { color: colors.text.secondary },
  upgradePill: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: withAlpha(colors.primary, 0.1),
  },
  upgradeText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  paneContainer: {
    flex: 1,
  },
  screenContainer: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  paneScrollContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  screenScrollContent: {
    padding: spacing.lg,
    paddingBottom: spacing.xl * 2,
  },
  paneHeader: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
  },
  screenHeader: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  subtitle: {
    marginTop: spacing.xs,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  closeButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionLabel: {
    fontSize: typography.fontSize.sm,
    textTransform: 'uppercase',
    color: colors.text.secondary,
    letterSpacing: 0.8,
    marginBottom: spacing.sm,
  },
  card: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
    backgroundColor: colors.background.surfaceRaised,
    gap: spacing.sm,
  },
  cardIconWrapper: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.primary, 0.07),
  },
  cardContent: {
    flex: 1,
  },
  cardTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  cardDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: 2,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.md,
  },
  versionRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.xs,
    paddingBottom: spacing.lg,
  },
  versionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  updateBtn: {
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  updateBtnText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
  },
  cardActive: {
    backgroundColor: withAlpha(colors.primary, 0.04),
    borderColor: colors.primary,
    borderWidth: 2,
  },
  cardIconWrapperActive: {
    backgroundColor: colors.primary,
  },
  cardTitleActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  dangerCard: {
    borderColor: withAlpha(colors.error, 0.19),
    borderWidth: 1,
  },
  dangerIcon: {
    backgroundColor: withAlpha(colors.error, 0.08),
  },
  dangerText: {
    color: colors.error,
  },
  langToggle: {
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    padding: 2,
    gap: 2,
  },
  langOption: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 6,
    minWidth: 40,
    alignItems: 'center',
  },
  langOptionActive: {
    backgroundColor: colors.primary,
  },
  langOptionText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  langOptionTextActive: {
    color: colors.white,
  },
});
